import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoginDialog from "./LoginDialog";
import {
  actionAttestationUpdated,
  actionAuthNotAuthorized,
  actionAuthShowSessionExpired,
  actionAuthSignInUser,
  actionAuthUpdated,
  cleanUrl,
  localStorageHasToken,
  startLoginFlow,
  urlShowsLoginPending,
  urlShowsNotAuthorized,
} from "./authRedux";
import { AuthProvider, useAuth } from "react-oidc-context";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { setupAxiosHandler } from "./axiosHandlers";
import { noteDismissedAlert, getUndismissedAlerts } from "./AlertsApi";
import { actionAddAlert, actionDeleteAlert } from "../Alert";
import { trackEvent } from "../../tracking";
import { needToShowAttestation } from "./AttestationApi";
import AttestationDialog from "./AttestationDialog";

function showAlerts(alerts, dispatch) {
  let counter = 1;
  alerts.forEach((alert) => {
    const alertId = `alert${counter++}`;
    dispatch(
      actionAddAlert({
        id: alertId,
        title: alert.message,
        severity: alert.severity,
        buttonLabel: "DISMISS",
        onClick: () => {
          noteDismissedAlert(alert);
          dispatch(actionDeleteAlert(alertId));
        },
        onClose: false,
      }),
    );
  });
}

function StartupBehaviorsGate() {
  const auth = useAuth();
  const dispatch = useDispatch();
  const hasLoggedIn = useRef(false);

  useEffect(() => {
    dispatch(actionAuthUpdated({ auth }));

    const shouldAutoLogin =
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      localStorageHasToken();

    if (auth.isAuthenticated) {
      // Get here after logging in successfully.
      // We CAN get into here multiple times; such as if the
      // oidc client library refreshes our token.
      console.log("User logged in:", new Date().toLocaleString());
      dispatch(actionAuthSignInUser(auth.user));

      if (!hasLoggedIn.current) {
        // This work we only need to do exactly once.
        hasLoggedIn.current = true;

        // In some login scenarios, we'll be left (by design) with a
        // URL with a query string like ?code=1234...
        // We need to remove this now.
        // However, if there happens to be a hash component (deep link)
        // we do not want to remove that.
        const keepHash = true;
        cleanUrl(keepHash);

        trackEvent({
          category: "User",
          action: "Login",
        });

        needToShowAttestation().then((showAttestation) => {
          dispatch(actionAttestationUpdated({ showAttestation }));
        });

        getUndismissedAlerts().then((alerts) => {
          showAlerts(alerts, dispatch);
        });
      }
    } else if (shouldAutoLogin) {
      // We'll get here if we have logged in previously BUT the JWT
      // is no longer valid. In this case, we'll be showing the
      // login dialog and we "act like" the user clicked the LOGIN
      // button.
      dispatch(startLoginFlow(auth));
    } else if (!auth.activeNavigator && urlShowsNotAuthorized()) {
      cleanUrl();
      dispatch(actionAuthNotAuthorized());
    }
  }, [
    auth.user,
    auth.isLoading,
    auth.activeNavigator,
    auth.isAuthenticated,
    dispatch,
  ]);

  return null;
}

const OIDC_BASE_URL = `https://${import.meta.env.VITE_AUTH_DOMAIN_PREFIX}.auth.us-east-1.amazoncognito.com`;
const userManager = new UserManager({
  // This property is unused since we supply everything needed in metadata;
  // which overrides usage of 'authority'.
  //
  // Even if unused, it still must be set to "something" since it is used
  // to form a key in local storage.
  authority: "mgis-oidc",

  client_id: import.meta.env.VITE_AUTH_CLIENT_ID,
  redirect_uri: `${window.location.origin}/`,

  // Using local storage to hold the session allows for different tabs to share
  // the session. (as opposed to sessionStorage which would not)
  userStore: new WebStorageStateStore({ store: window.localStorage }),

  // Disable silent renew to force the user to reauthenticate after the
  // initial JWT expires. Without this, it would use the refresh token to
  // silently renew the JWTs for a long time (at least a day).  The
  // default is 30 days and the shortest you can set this is 1 day.
  //
  // Forcing the user to reauth is consistent with our security policies.
  automaticSilentRenew: false,

  metadata: {
    // For login
    authorization_endpoint: `${OIDC_BASE_URL}/oauth2/authorize`,
    token_endpoint: `${OIDC_BASE_URL}/oauth2/token`,

    // For logout
    end_session_endpoint: `${OIDC_BASE_URL}/logout`,
  },
});

export default function Auth() {
  const dispatch = useDispatch();

  useEffect(() => {
    setupAxiosHandler(dispatch);
  }, [dispatch]);

  const user = useSelector((state) => state.authReducer.user);
  const status = useSelector((state) => state.authReducer.status);

  if (urlShowsLoginPending()) {
    // If the user somehow gets ahold of our URL with the ?code=123...
    // query string and tries to navigate to it directly
    // (not as part of an OIDC flow), then we want to erase this
    // query string.
    //
    // If we are here as part of a real OIDC flow, then cleanUrl()
    // will erase it anyway, but we still need this for the non-flow case.
    setTimeout(() => cleanUrl(), 500);
  }

  useEffect(() => {
    if (user && status === "expired") {
      dispatch(actionAuthShowSessionExpired());
    }
  }, [dispatch, user, status]);

  return (
    <AuthProvider userManager={userManager}>
      <StartupBehaviorsGate />
      <LoginDialog />
      <AttestationDialog />
    </AuthProvider>
  );
}
